import React from 'react';
import { Platform, type StyleProp, type ViewStyle } from 'react-native';

import { FormFieldStack, SubsectionForm } from './SubsectionForm';
import { colors, spacing } from '../../theme';

const isWeb = Platform.OS === 'web';

/*
  Create Request stacks many bordered forms (Opportunity, Site Walk, …). Each should share the same
  outer border, bottom spacing, and inner padding—otherwise one step looks wider/narrower than the next.

  Centralise that shell here so padding numbers are not copy-pasted. When
  `spacing.formSectionContentPaddingWeb` changed for parity, this component picked it up everywhere.
*/

/**
 * Default outer frame for create-request step forms: theme border + vertical gap before the next form.
 * Merge with `containerStyle` on `StepForm` for one-offs.
 */
export const stepFormStyle: ViewStyle = {
  marginBottom: spacing.pdStepSubsectionGap,
  borderColor: colors.borderCard,
};

/**
 * Web-only inner padding for the white card body. Uses {@link spacing.formSectionContentPaddingWeb}
 * on **all four sides** on purpose: if bottom padding were smaller, the last field + character counter
 * would look “clipped” compared to sibling forms. Keeping top/bottom symmetric makes QA snapshots stable.
 */
const webContentTight: ViewStyle = {
  ...Platform.select({
    web: {
      paddingTop: spacing.formSectionContentPaddingWeb,
      paddingHorizontal: spacing.formSectionContentPaddingWeb,
      paddingBottom: spacing.formSectionContentPaddingWeb,
    },
    default: {},
  }),
};

type SubsectionFormBaseProps = React.ComponentProps<typeof SubsectionForm>;

export type StepFormProps = Omit<
  SubsectionFormBaseProps,
  'containerStyle' | 'contentContainerStyle' | 'children'
> & {
  children: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /**
   * When true (default), children are wrapped in `FormFieldStack` (16px vertical gap).
   * Set false to compose your own `FormFieldStack` or non-field content.
   */
  withFieldStack?: boolean;
  fieldStackStyle?: StyleProp<ViewStyle>;
  /** Renders above the field stack (e.g. offline + inline validation). */
  leading?: React.ReactNode;
};

/**
 * Bordered form shell for a Create Request step: wraps `SubsectionForm` with shared border/margin,
 * web content insets, and optional `FormFieldStack` body. Pair field controls with `formFieldInStack`.
 *
 * **#1011:** Titled cards collapse by default except the **first** collapsible card per step
 * (see `StepFirstSubsectionExpandProvider`). Applies across all tail steps.
 *
 * RHF-backed blocks: use `renderRequestFormRhfField` from `rhfFieldFactory` with this wrapper so
 * `Controller` + `PermissionAwareField` + `formFieldInStack` spacing stay consistent.
 */
export const StepForm: React.FC<StepFormProps> = ({
  children,
  containerStyle,
  contentContainerStyle,
  withFieldStack = true,
  fieldStackStyle,
  leading,
  ...rest
}) => {
  const webBody = isWeb
    ? [webContentTight, contentContainerStyle]
    : contentContainerStyle;

  return (
    <SubsectionForm
      containerStyle={[stepFormStyle, containerStyle]}
      contentContainerStyle={webBody}
      {...rest}
    >
      {leading}
      {withFieldStack ? (
        <FormFieldStack containerStyle={fieldStackStyle}>
          {children}
        </FormFieldStack>
      ) : (
        children
      )}
    </SubsectionForm>
  );
};

StepForm.displayName = 'StepForm';
